
import GeoJSON from 'ol/format/GeoJSON';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS.js';
import {bbox as bboxStrategy} from 'ol/loadingstrategy';
import Projection from 'ol/proj/Projection';
import {get as getProjection} from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS.js';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import WMTS from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';

import { createStyle } from './olStyles';

const SOURCE_CLASSES = {
    'TileWMS': {
        layer: TileLayer,
        source: TileWMS
    },
    'ImageWMS': {
        layer: ImageLayer,
        source: ImageWMS
    },
    'WMTS': {
        layer: TileLayer,
        source: WMTS
    },
    'GeoJson': {
        layer: VectorLayer,
        source: VectorSource
    },
    'Vector': {
        layer: VectorLayer,
        source: VectorSource
    }
};

function createLayer(layer) {
    let layerOptions = createLayerOptions(layer);
    return new SOURCE_CLASSES[layer.type].layer(layerOptions);
}

function createLayerOptions(layer) {
    switch (layer.type) {
        case 'Vector':
            return createLayerOptionsGeoJson(layer);
        case 'GeoJson':
            return createLayerOptionsGeoJson(layer);
        default:
            return createLayerOptionsDefault(layer);
    }
}

function createLayerOptionsDefault(layer) {
    return {
        id: layer.id,
        maxResolution: layer.maxResolution,
        minResolution: layer.minResolution,
        opacity: layer.opacity || 1.0,
        source: createLayerSource(layer)
    };
}

function createLayerOptionsGeoJson(layer) {
    let options = createLayerOptionsDefault(layer);
    if (layer.style) {
        if (layer.style.default) {
            options.style = createStyle(layer.style.default);
        }
        if (layer.style.highlight) {
            options.highlightStyle = createStyle(layer.style.highlight);
        }
    }
    return options;
}

function createLayers(layers) {
    return layers.map(item => {
        let layer = createLayer(item);
        return layer;
    });
}

function createLayerSource(layer) {
    switch (layer.type) {
        case 'GeoJson':
            return createLayerSourceGeoJson(layer);
        case 'Vector':
            return createLayerSourceVector(layer);
        case 'WMTS':
            return createLayerSourceWmts(layer);
        default:
            let sourceOptions = {
                ...layer.source,
                crossOrigin: 'Anonymous'
            };
            return new SOURCE_CLASSES[layer.type].source(sourceOptions);
    }
}

function createLayerSourceGeoJson(layer) {
    let source = new VectorSource({
        ...layer.source,
        format: new GeoJSON(),
        url: function(extent, resolution, projection) {
            return layer.source.url + '&bbox=' + extent.join(',');
        },
        strategy: bboxStrategy,
    });
    source.on('addfeature', (evt) => {
        evt.feature.layerId = layer.id;
    });
    return source;
}

function createLayerSourceVector(layer) {
    let source = new VectorSource({
        ...layer.source
    });
    source.on('addfeature', (evt) => {
        evt.feature.layerId = layer.id;
    });
    return source;
}

function createLayerSourceWmts(layer) {
    return new WMTS({
        ...layer.source.params,
        crossOrigin: 'Anonymous',
        tileGrid: new WMTSTileGrid({
            ...layer.source.tileGrid,
            projection: getProjection(layer.source.tileGrid.projection)
        })
    });
}

function createProjection(projectionCode) {
    return new Projection({
        code: projectionCode,
        units: 'm'
    });
}

export {
    createLayer,
    createLayers,
    createProjection
}
